import { useMemo, useState } from 'react';

import {
  Address,
  offlineStoreDataGuard,
  onlineStoreDataGuard,
  type StoreData,
  type AgentFormValues,
  validatePointsOfSales,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_STORES,
} from '~domains/partner-application/shared/constants';
import { getApplicationEventProperties } from '~domains/partner-application/shared/lib';
import { ItemList } from '~domains/partner-application/shared/ui/item-list';

import { ampli } from '~shared/analytics';
import { useConfirmation } from '~shared/ui/confirmation';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Button } from '@breezeventures/ui-kit/button';
import { Icon } from '@breezeventures/ui-kit/next/icon';
import { Typography } from '@breezeventures/ui-kit/typography';

const formatAddress = (address?: Address) => {
  if (!address) return '';
  const parts = [
    address.streetAndHouse,
    address.barangay,
    address.cityOrMunicipality,
    address.province,
    address.region,
    address.postalCode,
  ].filter(Boolean);
  return parts.join(', ');
};

type StoresListProps = {
  applicationId?: string;
  stores?: StoreData[];
  agents?: AgentFormValues[];
  onBack: () => void;
  onClose: () => void;
  onSubmit: () => void;
  onEditStore: (storeId: string) => void;
  onAddStore: () => void;
  onRemoveStore: (storeId: string) => Promise<void>;
};

export const StoresList = ({
  applicationId,
  stores = [],
  agents = [],
  onBack,
  onClose,
  onSubmit,
  onEditStore,
  onAddStore,
  onRemoveStore,
}: StoresListProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const offlineStores = stores.filter(offlineStoreDataGuard);
  const { confirm } = useConfirmation();

  const storeErrors = useMemo(() => {
    const errors = validatePointsOfSales(stores ?? []);
    if (errors.general) {
      return {};
    }

    return errors;
  }, [stores]);

  const items =
    stores?.map((store) => {
      const secondaryText = onlineStoreDataGuard(store)
        ? store.details.websiteURL
        : store.details.websiteURL || formatAddress(store.address);
      const errors = storeErrors[store.details.uiId];
      const errorText = errors?.length
        ? "This store's info is incomplete. Update or delete the store."
        : undefined;

      return {
        id: store.details.uiId,
        primaryText: store.details.name,
        secondaryText: secondaryText || undefined,
        ...(errorText && { errorText }),
      };
    }) ?? [];

  const handleRemoveStore = async (storeId: string) => {
    const isLastOfflineStore =
      offlineStores.length === 1 && offlineStores[0].details.uiId === storeId;
    const shouldRemoveAgents = isLastOfflineStore && !!agents.length;

    try {
      await confirm({
        title: 'Are you sure you want to delete this store?',
        description: shouldRemoveAgents
          ? 'This is your only physical store. All assigned agents will also be deleted.'
          : 'All information for this store will be permanently removed.',
        confirmButtonText: 'Yes, delete the store',
      });
      ampli.mpPaDeleteStoreDialogConfirmClick(
        getApplicationEventProperties(applicationId)
      );
    } catch {
      ampli.mpPaDeleteStoreDialogCancelClick(
        getApplicationEventProperties(applicationId)
      );
      return;
    }

    try {
      setIsUpdating(true);
      await onRemoveStore(storeId);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Screen
      header={<ScreenHeader onBack={onBack} onClose={onClose} />}
      footer={
        <FooterWithProgressBar
          currentStepIndex={STEP_INDEX_STORES}
          totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
          onSubmit={onSubmit}
          isLoading={isUpdating}
        />
      }
    >
      <div className="flex flex-col gap-3 py-6">
        <Typography.Title size="s">
          Add information about your stores
        </Typography.Title>
        <Typography.Text size="m" color="secondary">
          List all the stores where you want to offer Skyro
        </Typography.Text>
      </div>

      <ItemList
        items={items}
        onEdit={onEditStore}
        onRemove={handleRemoveStore}
      />

      <Button
        type="button"
        size="l"
        variant="secondary"
        width="full"
        onClick={onAddStore}
      >
        <Icon name="plus" size={24} version="v1" />
        Add store
      </Button>
    </Screen>
  );
};
